import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "context/AuthContext";
import lock from "assets/images/illustrations/lock.png";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import AuthenticationLayout from "layouts/authenticationLayout/index";
import * as cognitoAuth from "services/cognitoAuth";

function SetNewPassword() {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [passwordRequirements, setPasswordRequirements] = useState({
    minLength: false,
    hasUpperCase: false,
    hasLowerCase: false,
    hasNumber: false,
    hasSpecialChar: false,
  });

  // Get email from location state
  useEffect(() => {
    const emailFromState = location.state?.email;
    if (emailFromState) {
      setEmail(emailFromState);
    } else {
      // If no email in state, redirect back to sign-in
      navigate("/sign-in", { replace: true });
    }
  }, [location.state, navigate]);

  // Redirect if already authenticated (layout uses profile from getUserProfile)
  useEffect(() => {
    if (auth.isAuthenticated && auth.user) {
      navigate("/dashboard", { replace: true });
    }
  }, [auth.isAuthenticated, auth.user, navigate]);

  // Validate password requirements
  useEffect(() => {
    setPasswordRequirements({
      minLength: newPassword.length >= 8,
      hasUpperCase: /[A-Z]/.test(newPassword),
      hasLowerCase: /[a-z]/.test(newPassword),
      hasNumber: /[0-9]/.test(newPassword),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(newPassword),
    });
  }, [newPassword]);

  const validatePassword = () => {
    if (newPassword.length < 8) {
      return "Password must be at least 8 characters long.";
    }
    if (!/[A-Z]/.test(newPassword)) {
      return "Password must contain at least one uppercase letter.";
    }
    if (!/[a-z]/.test(newPassword)) {
      return "Password must contain at least one lowercase letter.";
    }
    if (!/[0-9]/.test(newPassword)) {
      return "Password must contain at least one number.";
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(newPassword)) {
      return "Password must contain at least one special character.";
    }
    if (newPassword !== confirmPassword) {
      return "Passwords do not match.";
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validate password
    const validationError = validatePassword();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSubmitting(true);

    try {
      // Ensure email and password are strings
      const emailString = String(email).trim();
      const passwordString = String(newPassword).trim();
      
      if (!emailString || !passwordString) {
        setError("Email and password are required.");
        return;
      }
      
      // Complete the new password challenge
      const userData = await cognitoAuth.completeNewPassword(
        emailString,
        passwordString
      );

      // Update auth context with the new user data
      // Manually set the user in auth context since we have the data
      if (auth.setUser) {
        auth.setUser(userData);
      } else {
        // Fallback: wait a moment for Cognito to store the session, then check
        await new Promise((resolve) => setTimeout(resolve, 500));
        try {
          await auth.checkAuth();
        } catch (e) {
          // If checkAuth fails, still use the userData we have
          console.warn("Could not verify session, using provided user data");
        }
      }

      navigate("/dashboard", { replace: true });
    } catch (err) {
      let errorMessage = "Failed to set new password. Please try again.";

      if (err.code === "InvalidPasswordException") {
        errorMessage = "Password does not meet requirements.";
      } else if (err.code === "NotAuthorizedException") {
        errorMessage = "Invalid credentials. Please try signing in again.";
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const allRequirementsMet = Object.values(passwordRequirements).every(
    (req) => req === true
  );

  return (
    <AuthenticationLayout
      title="Set New Password"
      description="Please set a new password for your account"
      illustration={{
        image: lock,
        title: "Security First",
        description:
          "Choose a strong password to protect your account. This is your first time signing in.",
      }}
    >
      <SoftBox component="form" role="form" onSubmit={handleSubmit}>
        {error && (
          <SoftBox mb={2} p={2} bgColor="error" borderRadius="lg">
            <SoftTypography variant="body2" color="white">
              {error}
            </SoftTypography>
          </SoftBox>
        )}

        <SoftBox mb={2}>
          <SoftInput
            type="email"
            placeholder="Email"
            size="large"
            value={email}
            disabled
            sx={{ bgColor: "grey.100" }}
          />
        </SoftBox>

        <SoftBox mb={2}>
          <SoftInput
            type="password"
            placeholder="New Password"
            size="large"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            disabled={isSubmitting}
          />
          {newPassword && (
            <SoftBox mt={1}>
              <SoftTypography variant="caption" color="text" fontWeight="medium">
                Password Requirements:
              </SoftTypography>
              <SoftBox component="ul" mt={0.5} pl={2}>
                <SoftBox
                  component="li"
                  sx={{
                    color: passwordRequirements.minLength ? "success.main" : "text.secondary",
                  }}
                >
                  <SoftTypography variant="caption">
                    At least 8 characters
                  </SoftTypography>
                </SoftBox>
                <SoftBox
                  component="li"
                  sx={{
                    color: passwordRequirements.hasUpperCase ? "success.main" : "text.secondary",
                  }}
                >
                  <SoftTypography variant="caption">
                    One uppercase letter
                  </SoftTypography>
                </SoftBox>
                <SoftBox
                  component="li"
                  sx={{
                    color: passwordRequirements.hasLowerCase ? "success.main" : "text.secondary",
                  }}
                >
                  <SoftTypography variant="caption">
                    One lowercase letter
                  </SoftTypography>
                </SoftBox>
                <SoftBox
                  component="li"
                  sx={{
                    color: passwordRequirements.hasNumber ? "success.main" : "text.secondary",
                  }}
                >
                  <SoftTypography variant="caption">One number</SoftTypography>
                </SoftBox>
                <SoftBox
                  component="li"
                  sx={{
                    color: passwordRequirements.hasSpecialChar ? "success.main" : "text.secondary",
                  }}
                >
                  <SoftTypography variant="caption">
                    One special character
                  </SoftTypography>
                </SoftBox>
              </SoftBox>
            </SoftBox>
          )}
        </SoftBox>

        <SoftBox mb={2}>
          <SoftInput
            type="password"
            placeholder="Confirm New Password"
            size="large"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            disabled={isSubmitting}
          />
          {confirmPassword && newPassword !== confirmPassword && (
            <SoftBox mt={1}>
              <SoftTypography variant="caption" color="error">
                Passwords do not match
              </SoftTypography>
            </SoftBox>
          )}
        </SoftBox>

        <SoftBox mt={4} mb={1}>
          <SoftButton
            type="submit"
            variant="contained"
            color="primary"
            size="large"
            fullWidth
            disabled={
              isSubmitting ||
              !newPassword ||
              !confirmPassword ||
              !allRequirementsMet ||
              newPassword !== confirmPassword
            }
          >
            {isSubmitting ? "Setting password..." : "Set New Password"}
          </SoftButton>
        </SoftBox>

        <SoftBox mt={3} textAlign="center">
          <SoftTypography variant="button" color="text" fontWeight="regular">
            Remember your password?{" "}
            <SoftTypography
              component={Link}
              to="/sign-in"
              variant="button"
              color="secondary"
              fontWeight="medium"
            >
              Sign in
            </SoftTypography>
          </SoftTypography>
        </SoftBox>
      </SoftBox>
    </AuthenticationLayout>
  );
}

export default SetNewPassword;

